import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { OrderSummary, clearUserCartLocalStorage, calculateGrandTotal, BackToCartButton, PaymentPrintReceipt } from "../components/ReusableComponents";


const handlePrint = () => {
    const printContent = document.getElementById("thankyou-print-section").innerHTML;
    const win = window.open("", "_blank", "width=900,height=650");
    win.document.write(`
        <html>
            <head>
                <title>Receipt</title>
                <link rel="stylesheet" href="/wp-content/themes/dom/assets/css/print.css" media="print" />
            </head>

            <body>
                ${printContent}
            </body>
        </html>
    `);
    win.document.close();
    win.onload = () => {
        win.focus();
        win.print();
    };
};


const InvoiceThankyou = () => {
    const im_custom = window.im_ecom || {};
    const supportEmail = im_custom.support_email ?? "";
    const [grandTotal, setGrandTotal] = useState(0);

    // Track if cart has been initialized (loaded from localStorage or has content)
    const isInitializedRef = useRef(false);

    const [userCart, setUserCart] = useState({
        users: [],
        attendees: [],
        activePlan: null,
        activeEvent: null,
        isAgreed: "0",
    });
    const [paymentStatus, setPaymentStatus] = useState(null); // default to success

    const [loading, setLoading] = useState(true);
    const [orderId, setOrderId] = useState('');
    const [taxAmount, setTaxAmount] = useState(0);

    useEffect(() => {
        // Load cart data
        const saved = localStorage.getItem("user_cart");
        if (saved) {
            clearUserCartLocalStorage();
        } else {
            window.location.href = '/';
        }
        const savedParse = saved ? JSON.parse(saved) : null;
        if (savedParse) {
            console.log("Loaded saved cart:", savedParse);
            setUserCart(savedParse);
            // Mark as initialized if cart has content
            if (savedParse.activePlan || savedParse.activeEvent) {
                isInitializedRef.current = true;
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        // console.log("User cart changed:", userCart);
        setGrandTotal(calculateGrandTotal(userCart, im_custom));

        // Mark as initialized when cart gets content
        if (!isInitializedRef.current && (userCart.activePlan || userCart.activeEvent)) {
            isInitializedRef.current = true;
        }

        // Only persist if cart is initialized and has content
        if (!isInitializedRef.current) return;
        if (!userCart.activePlan && !userCart.activeEvent) return;

        // localStorage.setItem("user_cart", JSON.stringify(userCart));
    }, [userCart]);

    useEffect(() => {
        // Payment is already confirmed before navigation to this page
        // (polling happens in PaymentForm component)
        setPaymentStatus('succeeded');
    }, []);

    return (
        <>
            <div style={{ display: "none" }}>
                <PaymentPrintReceipt im_custom={im_custom} userCart={userCart} orderId={orderId} grandTotal={grandTotal} estimatedTax={taxAmount}
                    receiptHeading="Thank You – Invoice Payment Requested."
                    receiptParagraph="Since you’ve chosen to pay by invoice, a member of the Directions on Microsoft team will reach out shortly to complete your payment."
                />
            </div>
            <div className="about-wrapper">
                <div className="about-container cart-main checkout-main thank-you-page">

                    <div className="row justify-content-space-between">
                        <div className="col-lg-8">
                            <h1>Thank You – Invoice Payment Requested.</h1>
                            <p className="public-desc">
                                Since you’ve chosen to pay by invoice, a member of the Directions on Microsoft team will reach out shortly to complete your payment.
                            </p>

                            {orderId ? (
                                <p className="public-desc">Order ID#: {orderId}</p>
                            ) : null}

                            {userCart.activePlan?.plan_label && (
                                <div className="standard">
                                    <div>
                                        <h3>
                                            {userCart.activePlan?.plan_label && `Plan : ${userCart.activePlan.plan_label}`}
                                        </h3>
                                    </div>
                                </div>
                            )}

                            {userCart.activePlan && userCart.activePlan.plan_id && (
                                <div className="research">
                                    <div className="row">
                                        <div className="col-2 col-lg-3 col-xl-2 mb-2 mb-lg-4">
                                            <div className="image-wrap">
                                                <img src="/wp-content/themes/dom/assets/img/atlas-laptop.webp"
                                                    alt="Atlas Membership" />
                                            </div>
                                        </div>
                                        <div className="col-3 col-lg-9 col-xl-4 mb-2 mb-lg-4">
                                            <div className="research-one">
                                                <div>
                                                    <h3>
                                                        Atlas <br /> Membership
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-4 col-lg-6 col-xl-3 mb-2 mb-lg-4">
                                            <div className="research-one">
                                                <div>
                                                    {(() => {
                                                        const totalUsers = userCart.users?.length || 0;
                                                        const defaultUsers = userCart.users?.filter((u) => u.isDefault === "1").length || 0;
                                                        const additionalUsers = userCart.users?.filter((u) => u.isDefault === "0").length || 0;
                                                        const assignedUsers =
                                                            userCart.users?.filter(
                                                                (u) =>
                                                                    (u.firstName && u.firstName.trim() !== "") &&
                                                                    (u.lastName && u.lastName.trim() !== "") &&
                                                                    (u.email && u.email.trim() !== "")
                                                            ).length || 0;

                                                        const unassignedUsers = totalUsers - assignedUsers;

                                                        return (
                                                            <>
                                                                <div className="lnfo mb-1">
                                                                    {assignedUsers} of {totalUsers}{" "}
                                                                    {totalUsers === 1 ? "User" : "Users"} Assigned
                                                                </div>

                                                                {unassignedUsers > 0 ? (
                                                                    <p>
                                                                        You have {unassignedUsers} unassigned{" "}
                                                                        {unassignedUsers === 1 ? "user" : "users"}. You can assign them later by{" "}
                                                                        <a href={`mailto:${supportEmail}?subject=Assign%20Users%20Request`}>emailing us</a>.
                                                                    </p>
                                                                ) : (
                                                                    <p>All users are assigned</p>
                                                                )}

                                                                {/* Assigned users list */}
                                                                <div className="assigned-attendees mt-2">
                                                                    <h5>Assigned Users:</h5>
                                                                    {userCart.users
                                                                        .filter((a) => a.leaveSeatUnassigned === "0")
                                                                        .map((a, i) => (
                                                                            <div key={i}>
                                                                                {a.firstName} {a.lastName} ({a.email})
                                                                            </div>
                                                                        ))}

                                                                    {userCart.users.filter((a) => a.leaveSeatUnassigned === "0").length === 0 && (
                                                                        <div>No assigned users.</div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-3 col-lg-6 col-xl-3 mb-2 mb-lg-4">
                                            <div className="research-one">
                                                <div className="w-100">
                                                    <a href="/atlas" className="custom-button ms-md-auto">
                                                        Launch Atlas
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {userCart?.attendees?.length > 0 && (
                                <div className="standard">
                                    <div>
                                        <h3>
                                            {userCart.activeEvent?.title ? `Event : ${userCart.activeEvent.title}` : 'Event : Not selected'}
                                        </h3>
                                    </div>
                                </div>
                            )}

                            {userCart?.attendees?.length > 0 && (<div className="research">
                                <div className="row">
                                    <div className="col-2 col-lg-3 col-xl-2 mb-2 mb-lg-4">
                                        <div className="image-wrap">
                                            <img src="/wp-content/themes/dom/assets/img/LBC-ticket-thumbnail.png"
                                                alt="Atlas Membership" />
                                        </div>
                                    </div>
                                    <div className="col-3 col-lg-9 col-xl-4 mb-2 mb-lg-4">
                                        <div className="research-one">
                                            <div>
                                                <h3>
                                                    Microsoft Licensing <br /> Training
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-lg-6 col-xl-3 mb-2 mb-lg-4">
                                        <div className="research-one">
                                            <div>
                                                {(() => {
                                                    const totalAttendees = userCart.attendees?.length || 0;
                                                    const assignedAttendees =
                                                        userCart.attendees?.filter((a) => a.leaveSeatUnassigned != "1").length || 0;
                                                    const remainingAttendees = totalAttendees - assignedAttendees;
                                                    if (totalAttendees === 0 && assignedAttendees === 0) {
                                                        return (
                                                            <div className="lnfo mb-1">
                                                                0 of 0 Attendees Assigned
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <>
                                                            <div className="lnfo mb-1">
                                                                {assignedAttendees} of {totalAttendees}{" "}
                                                                {totalAttendees === 1 ? "Attendee" : "Attendees"} Assigned
                                                            </div>

                                                            {remainingAttendees > 0 ? (
                                                                <p>
                                                                    You can still assign {remainingAttendees}{" "}
                                                                    {remainingAttendees === 1 ? "attendee" : "attendees"} later by{" "}
                                                                    <a href={`mailto:${supportEmail}?subject=Assign%20Attendees%20Request`}>emailing us</a>.
                                                                </p>
                                                            ) : (
                                                                <p>All attendees are assigned</p>
                                                            )}

                                                            {/* Assigned attendees list */}
                                                            <div className="assigned-attendees mt-2">
                                                                <h5>Assigned Attendees:</h5>
                                                                {userCart.attendees
                                                                    .filter((a) => a.leaveSeatUnassigned === "0")
                                                                    .map((a, i) => (
                                                                        <div key={i}>
                                                                            {a.firstName} {a.lastName} ({a.email})
                                                                        </div>
                                                                    ))}

                                                                {userCart.attendees.filter((a) => a.leaveSeatUnassigned === "0").length === 0 && (
                                                                    <div>No assigned attendees.</div>
                                                                )}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>)}

                            <div className="payment-information">
                                <div className="payment-information-card">
                                    <h3 className="payment-information-heading">Purchased by</h3>
                                    <div className="row">
                                        <div className="col-sm-5 col-lg-4">
                                            <p className="public-desc">{im_custom.user.name}</p>
                                            <p className="public-desc">{im_custom.user.email}</p>
                                            <p className="public-desc">{im_custom.user.phone}</p>
                                        </div>
                                        <div className="col-sm-5 col-lg-4">
                                            <p className="public-desc">
                                                {im_custom.user.company} <br /> {im_custom.user.country}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xxl-3 col-md-4">
                            <OrderSummary
                                userCart={userCart}
                                im_custom={im_custom}
                                estimatedTax={taxAmount}
                                grandTotal={grandTotal}
                                continueButtonText={'Print Receipt'}
                                continueButtonHandle={handlePrint}
                            />
                            <div className="order-summary mt-3 text-center">
                                <h3>Have questions?</h3>
                                <a href="/form/?form_id=18&campaign=Invoice_Contact_Us" className="custom-button w-100">
                                    Contact Us
                                </a>
                            </div>
                        </div>

                    </div>

                </div>
            </div>
        </>
    );
}
export default InvoiceThankyou;